import React, { useMemo, useState, useEffect } from "react";
import {
  Grid,
  Card,
  Box,
  Typography,
  Chip,
  Button,
  CircularProgress,
  Pagination,
  Stack,
} from "@mui/material";
import * as MuiIcons from "@mui/icons-material";
import AssignmentIcon from "@mui/icons-material/Assignment";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useTheme } from "@mui/material/styles";

const ALLOWED_PALETTE_KEYS = new Set([
  "primary",
  "secondary",
  "error",
  "warning",
  "info",
  "success",
]);

const ITEMS_PER_PAGE = 10;

const normalizePaletteKey = (value, fallback = "secondary") => {
  const key = String(value || "").toLowerCase().trim();
  if (key === "inherit") return fallback;
  return ALLOWED_PALETTE_KEYS.has(key) ? key : fallback;
};

const formatDate = (date) => {
  if (!date) return "—";
  return new Date(date).toLocaleDateString();
};

const WorkOrdersList = ({
  workOrders,
  statuses,
  navigate,
  clubMemberCode,
  loading = false,
}) => {
  const theme = useTheme();
  const [page, setPage] = useState(1);

  useEffect(() => {
    setPage(1);
  }, [workOrders]);

  const statusMap = useMemo(() => {
    const map = {};
    (statuses || []).forEach((status) => {
      const key = String(status.statusname || "").toLowerCase().trim();
      map[key] = status;
    });
    return map;
  }, [statuses]);

  const getStatusInfo = (statusName) => {
    const key = String(statusName || "").toLowerCase().trim();
    return statusMap[key];
  };

  const totalPages = Math.ceil((workOrders?.length || 0) / ITEMS_PER_PAGE);

  const paginatedWorkOrders = useMemo(() => {
    const startIndex = (page - 1) * ITEMS_PER_PAGE;
    return (workOrders || []).slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [workOrders, page]);

  const pageCardSx = {
    borderRadius: "18px",
    border: "1px solid rgba(47,99,173,0.08)",
    boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
    backgroundColor: "rgba(255,255,255,0.96)",
  };

  const secondaryButtonSx = {
    borderRadius: "10px",
    textTransform: "none",
    fontWeight: 600,
    px: 2,
    borderColor: "rgba(47,99,173,0.18)",
    color: "#223142",
    whiteSpace: "nowrap",
    "&:hover": {
      borderColor: "#2f63ad",
      color: "#2f63ad",
      backgroundColor: "rgba(47,99,173,0.04)",
    },
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "180px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!workOrders || workOrders.length === 0) {
    return (
      <Card sx={{ ...pageCardSx, p: 3, textAlign: "center" }}>
        <Typography variant="body1" sx={{ fontWeight: 600, color: "#223142" }}>
          No work orders found
        </Typography>
        <Typography variant="body2" sx={{ color: "#6b778c", mt: 0.75 }}>
          This member does not currently have any recorded work orders.
        </Typography>
      </Card>
    );
  }

  return (
    <Box>
      <Grid container spacing={2.25}>
        {paginatedWorkOrders.map((order) => {
          const statusInfo = getStatusInfo(order.status);
          const paletteKey = normalizePaletteKey(
            statusInfo?.color,
            order.resolved_at ? "success" : "error"
          );
          const IconComponent =
            statusInfo?.icon && MuiIcons[statusInfo.icon]
              ? MuiIcons[statusInfo.icon]
              : AssignmentIcon;

          const borderColor =
            theme.palette[paletteKey]?.main ||
            (order.resolved_at ? "#4caf50" : "#f44336");

          return (
            <Grid item xs={12} key={order.order_id}>
              <Card
                sx={{
                  ...pageCardSx,
                  p: { xs: 1.5, sm: 2, md: 2.25 },
                  borderLeft: "6px solid",
                  borderLeftColor: borderColor,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: { xs: "flex-start", md: "center" },
                    gap: 2,
                    flexDirection: { xs: "column", md: "row" },
                  }}
                >
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Stack
                      direction="row"
                      spacing={1.25}
                      alignItems="center"
                      sx={{ mb: 1 }}
                    >
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: "12px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          backgroundColor: "rgba(47,99,173,0.08)",
                          flexShrink: 0,
                        }}
                      >
                        <IconComponent sx={{ color: "#2f63ad" }} fontSize="small" />
                      </Box>

                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: 700,
                            color: "#223142",
                            lineHeight: 1.2,
                          }}
                        >
                          WO-{order.order_id}
                        </Typography>

                        <Typography
                          variant="body2"
                          sx={{
                            color: "#6b778c",
                            mt: 0.25,
                            wordBreak: "break-word",
                          }}
                        >
                          {order.description || "No description available"}
                        </Typography>
                      </Box>
                    </Stack>

                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={{ xs: 0.75, sm: 2.5 }}
                      sx={{ pl: { xs: 0, sm: "52px" } }}
                    >
                      <Typography variant="body2" sx={{ color: "#6b778c" }}>
                        <Box
                          component="span"
                          sx={{ fontWeight: 700, color: "#223142" }}
                        >
                          Created:
                        </Box>{" "}
                        {formatDate(order.created_at)}
                      </Typography>

                      <Typography variant="body2" sx={{ color: "#6b778c" }}>
                        <Box
                          component="span"
                          sx={{ fontWeight: 700, color: "#223142" }}
                        >
                          Status:
                        </Box>{" "}
                        {statusInfo?.statusname || order.status || "Unknown"}
                      </Typography>

                      <Typography variant="body2" sx={{ color: "#6b778c" }}>
                        <Box
                          component="span"
                          sx={{ fontWeight: 700, color: "#223142" }}
                        >
                          Resolved:
                        </Box>{" "}
                        {order.resolved_at ? formatDate(order.resolved_at) : "No"}
                      </Typography>
                    </Stack>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: { xs: "stretch", sm: "center" },
                      gap: 1.25,
                      flexDirection: { xs: "column", sm: "row" },
                      width: { xs: "100%", md: "auto" },
                    }}
                  >
                    <Chip
                      label={String(
                        statusInfo?.statusname || order.status || "Unknown"
                      ).toUpperCase()}
                      color={paletteKey}
                      sx={{
                        fontWeight: 700,
                        color: "#fff",
                        height: 32,
                        borderRadius: "999px",
                        "& .MuiChip-label": {
                          px: 1.25,
                        },
                      }}
                    />

                    <Button
                      variant="outlined"
                      endIcon={<ArrowForwardIcon />}
                      onClick={() =>
                        navigate(`/home/workorders/${order.order_id}`, {
                          state: {
                            from: `/home/clubmembers/${clubMemberCode}`,
                            tabIndex: 2,
                          },
                        })
                      }
                      sx={secondaryButtonSx}
                    >
                      View Details
                    </Button>
                  </Box>
                </Box>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {workOrders.length > ITEMS_PER_PAGE && (
        <Box
          sx={{
            mt: 3,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Typography variant="body2" sx={{ color: "#6b778c" }}>
            Showing {(page - 1) * ITEMS_PER_PAGE + 1}–
            {Math.min(page * ITEMS_PER_PAGE, workOrders.length)} of{" "}
            {workOrders.length} work orders
          </Typography>

          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, value) => setPage(value)}
            color="primary"
            shape="rounded"
            siblingCount={1}
            boundaryCount={1}
            sx={{
              "& .MuiPaginationItem-root": {
                borderRadius: "10px",
                fontWeight: 600,
              },
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default WorkOrdersList;